import { RecurrenceInputBlock } from '@coast/schemas';
import { isEmpty } from 'lodash';
import { ReactNode, useCallback, useContext, useMemo } from 'react';
import { ScrollView } from 'react-native';
import { Frequency, RRule } from 'rrule';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { MBIconV2Name } from '@shared/components/image';
import { ColorThemeContext, RECURRENCE_CARD_COUNT, grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useNavStack } from '@shared/util/navigation';
import { useUpdateRecurrence } from '../hooks';
import * as RecurrencePresets from '../presets';
import { CustomSelectionType } from './CustomSelectionType';
import { RecurrencePickerRouteNames, RecurrencePickerRoutes } from './RecurrencePickerRoutes';
import { DailyRecurrencePicker } from './components/DailyRecurrencePicker';
import { IntervalPicker } from './components/IntervalPicker';
import { MonthlyRecurrencePicker } from './components/MonthlyRecurrencePicker';
import { WeeklyRecurrencePicker } from './components/WeeklyRecurrencePicker';
import { YearlyRecurrencePicker } from './components/YearlyRecurrencePicker';
import { MBRecurrencePickerSelectCount } from './selectCount';
import { MBRecurrenceSelectEndDate } from './selectEndDate';
import { MBRecurrencePickerStart } from './start';

const WIDTH = 375;
const HEIGHT = 325 + grid(1);

interface Props {
  block: RecurrenceInputBlock;
  onClose(): void;
  anchorName: string;
}

export function MBRecurrencePicker({ block, onClose, anchorName }: Props) {
  const colors = useContext(ColorThemeContext);
  const { values } = useCardFormContext();
  const value = values[block.name];

  const handleChange = useUpdateRecurrence(block);

  const isDesktop = useIsDesktop();
  const nav = useNavStack<RecurrencePickerRouteNames>([{ name: 'start' }]);
  const routes = new RecurrencePickerRoutes();

  // @ts-expect-error -- strict migration
  const title = useMemo<string>(() => {
    // @ts-expect-error -- strict migration
    switch (nav.currentRoute.name) {
      case 'start':
        return block.label;
      case 'dailyRecurrence':
        return i18n('card.blocks.recurrenceInput.actionSheet.daily');
      case 'selectEndDate':
        return i18n('card.blocks.recurrenceInput.ends.specificDate.title');
      case 'selectCount':
        return i18n('card.blocks.recurrenceInput.ends.afterXTimes.title');
      case 'weeklyRecurrence':
        return i18n('card.blocks.recurrenceInput.actionSheet.weekly');
      case 'monthlyRecurrence':
        return i18n('card.blocks.recurrenceInput.actionSheet.monthly');
      case 'selectDailyInterval':
      case 'selectWeeklyInterval':
      case 'selectMonthlyInterval':
      case 'selectYearlyInterval':
        return i18n('card.blocks.recurrenceInput.actionSheet.repeatEvery');
      case 'yearlyRecurrence':
        return i18n('card.blocks.recurrenceInput.actionSheet.yearly');
    }
  }, [nav, block]);

  const onSelectCustom = (selectionType: CustomSelectionType) => {
    const currentRrule = !isEmpty(value) ? RRule.fromString(value) : RRule.fromString('');
    switch (selectionType) {
      case CustomSelectionType.DAILY:
        if (currentRrule.options.freq !== Frequency.DAILY) {
          handleChange(RecurrencePresets.daily());
        }
        break;
      case CustomSelectionType.WEEKLY:
        if (currentRrule.options.freq !== Frequency.WEEKLY) {
          handleChange(RecurrencePresets.weekly([]));
        }
        break;
      case CustomSelectionType.MONTHLY:
        if (currentRrule.options.freq !== Frequency.MONTHLY) {
          handleChange(RecurrencePresets.monthly());
        }
        break;
      case CustomSelectionType.YEARLY:
        if (currentRrule.options.freq !== Frequency.YEARLY) {
          handleChange(RecurrencePresets.yearly());
        }
    }
    nav.push({ name: routes.selectionTypeToRoute(selectionType) });
  };

  const children = useMemo<ReactNode>(() => {
    // @ts-expect-error -- strict migration
    switch (nav.currentRoute.name) {
      case 'start':
        return (
          <MBRecurrencePickerStart
            rruleString={value}
            onChange={handleChange}
            onSelectCustom={onSelectCustom}
            onSelectEndDate={() => nav.push({ name: 'selectEndDate' })}
            onSelectCount={() => nav.push({ name: 'selectCount' })}
          />
        );
      case 'dailyRecurrence':
        return <DailyRecurrencePicker rruleString={value} onChange={handleChange} nav={nav} />;
      case 'selectEndDate':
        return <MBRecurrenceSelectEndDate rruleString={value} onChange={handleChange} />;
      case 'selectCount':
        return (
          <MBRecurrencePickerSelectCount
            rruleString={value}
            maxCount={RECURRENCE_CARD_COUNT}
            onChange={handleChange}
          />
        );
      case 'weeklyRecurrence':
        return <WeeklyRecurrencePicker rruleString={value} onChange={handleChange} nav={nav} />;
      case 'monthlyRecurrence':
        return <MonthlyRecurrencePicker rruleString={value} onChange={handleChange} nav={nav} />;
      case 'yearlyRecurrence':
        return <YearlyRecurrencePicker rruleString={value} onChange={handleChange} nav={nav} />;
      case 'selectDailyInterval':
        return (
          <IntervalPicker
            rruleString={value}
            onChange={handleChange}
            selectionTypePluralKey={'card.blocks.recurrenceInput.actionSheet.days'}
          />
        );
      case 'selectWeeklyInterval':
        return (
          <IntervalPicker
            rruleString={value}
            onChange={handleChange}
            selectionTypePluralKey={'card.blocks.recurrenceInput.actionSheet.weeks'}
          />
        );
      case 'selectMonthlyInterval':
        return (
          <IntervalPicker
            rruleString={value}
            onChange={handleChange}
            selectionTypePluralKey={'card.blocks.recurrenceInput.actionSheet.months'}
          />
        );
      case 'selectYearlyInterval':
        return (
          <IntervalPicker
            rruleString={value}
            onChange={handleChange}
            selectionTypePluralKey={'card.blocks.recurrenceInput.actionSheet.years'}
          />
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, value, handleChange]);

  const renderTitleBar = useCallback(() => {
    const exitIconProps = isDesktop
      ? null
      : {
          rightIcon: 'exit' as MBIconV2Name,
          rightIconColor: colors.foregroundActive,
          onRightIconPress: onClose,
        };
    // @ts-expect-error -- strict migration
    if (nav.currentRoute.name === 'start') {
      return <AnchoredSheetTitleBar title={title} {...exitIconProps} />;
    }

    return (
      <AnchoredSheetTitleBar
        title={title}
        leftIcon="arrow-point-left"
        leftIconSize={grid(4)}
        onLeftIconPress={nav.pop}
        {...exitIconProps}
      />
    );
  }, [colors, onClose, title, nav, isDesktop]);

  return (
    <AnchoredSheet
      onClose={onClose}
      renderTitleBar={renderTitleBar}
      anchorName={anchorName}
      initialSize="full"
      style={isDesktop ? { minWidth: WIDTH, minHeight: HEIGHT } : undefined}
    >
      <ScrollView
        key={nav.currentRoute?.name ?? 'recurrence'}
        style={isDesktop && { flex: 1, width: WIDTH }}
      >
        {children}
      </ScrollView>
    </AnchoredSheet>
  );
}
